/**
 * Follow-up suggestions on Rocky answers (LLD "Next-Action Recommendation" §7).
 *
 * The Runtime sends one `follow_ups` SSE event at the end of a turn and stores
 * the same `choice_group` component in a `follow_up_suggestion` history row.
 * Both end up as `followUps` on the assistant message they follow, so live and
 * history render identically. A click sends the option's value with
 * `context.follow_up_id`; the resulting `user_query` row carries that id, which
 * restores the selected state on reload.
 */
import type { SessionMessage } from '../../client/GatewayClient';
import { parseChoiceGroup, type ChoiceGroupComponent } from '../uiComponents/schema';

/** `follow_ups` event payload version this build renders; others are ignored. */
export const FOLLOW_UPS_SCHEMA_VERSION = 2;

export interface FollowUps {
  /** The suggestion row's message_id; option ids are `<messageId>#<n>`. */
  messageId: string;
  component: ChoiceGroupComponent;
  /** Option chosen from this group, if any. */
  selectedId?: string;
}

interface HasFollowUps {
  role: string;
  followUps?: FollowUps;
}

/** The suggestion message an option id belongs to (`<messageId>#<n>`). */
export function followUpMessageId(optionId: string): string {
  const i = optionId.lastIndexOf('#');
  return i > 0 ? optionId.slice(0, i) : optionId;
}

function build(messageId: unknown, rawComponent: unknown): FollowUps | null {
  if (typeof messageId !== 'string' || !messageId) return null;
  const component = parseChoiceGroup(rawComponent);
  if (!component) return null;
  // Option ids must point back at this message, or a click would be recorded
  // against a group that does not exist.
  if (!component.data.options.every(o => followUpMessageId(o.id) === messageId)) return null;
  return { messageId, component };
}

/** Data of an SSE `follow_ups` event → FollowUps, or null if not renderable. */
export function parseFollowUpsEvent(data: Record<string, unknown>): FollowUps | null {
  if (data.schema_version !== FOLLOW_UPS_SCHEMA_VERSION) return null;
  return build(data.message_id, data.component);
}

/**
 * History: attach a `follow_up_suggestion` row to the assistant message right
 * before it. Rows of abstained / skipped turns have empty content and are
 * ignored, as is a row whose answer is not in `display` (e.g. on the far side
 * of a page boundary).
 */
export function attachFollowUpRow<T extends HasFollowUps>(
  display: T[],
  row: Pick<SessionMessage, 'message_id' | 'message_content'>,
): void {
  const last = display[display.length - 1];
  if (!last || last.role !== 'assistant' || last.followUps) return;
  const followUps = build(row.message_id, row.message_content);
  if (followUps) last.followUps = followUps;
}

/** History: a `user_query` sent by a click marks the option it came from. */
export function markFollowUpSelection<T extends HasFollowUps>(display: T[], followUpId: string | null | undefined): void {
  if (!followUpId) return;
  const messageId = followUpMessageId(followUpId);
  for (let i = display.length - 1; i >= 0; i--) {
    const fu = display[i].followUps;
    if (fu?.messageId === messageId) {
      if (fu.component.data.options.some(o => o.id === followUpId)) {
        display[i] = { ...display[i], followUps: { ...fu, selectedId: followUpId } };
      }
      return;
    }
  }
}

/**
 * Only the latest turn's follow-ups can be clicked: the group must be on the
 * last message, nothing may be streaming, and nothing may be chosen yet.
 */
export function isFollowUpInteractive(index: number, total: number, isLoading: boolean, followUps: FollowUps): boolean {
  return index === total - 1 && !isLoading && followUps.selectedId === undefined;
}
